import { useEffect, useRef, useState, useCallback } from "react";
import { io, Socket } from "socket.io-client";

const SOCKET_URL = import.meta.env.VITE_BACKEND_URL || (import.meta.env.PROD ? "https://api.constrigo.fr" : "http://localhost:3001");

export function useSocket() {
  const socketRef = useRef<Socket | null>(null);
  const [connected, setConnected] = useState(false);

  useEffect(() => {
    // Auth via httpOnly constrigo_token cookie — the WS middleware reads it
    // from the handshake. withCredentials is required for the browser to
    // attach the cookie to the upgrade request.
    const socket = io(SOCKET_URL, {
      path: "/ws",
      withCredentials: true,
      transports: ["websocket", "polling"],
      reconnection: true,
      reconnectionDelay: 1000,
      reconnectionAttempts: 10,
      extraHeaders: { "x-constrigo-app": "OPS" },
    });

    socket.on("connect", () => {
      console.log("[WS] Connected:", socket.id);
      setConnected(true);
    });

    socket.on("disconnect", () => {
      console.log("[WS] Disconnected");
      setConnected(false);
    });

    socket.on("connect_error", (err) => {
      console.error("[WS] Connection error:", err.message);
      setConnected(false);
    });

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, []);

  const joinConversation = useCallback((conversationId: string) => {
    socketRef.current?.emit("join:conversation", conversationId);
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    socketRef.current?.emit("leave:conversation", conversationId);
  }, []);

  const sendTyping = useCallback((conversationId: string, isTyping: boolean) => {
    socketRef.current?.emit("typing", { conversationId, isTyping });
  }, []);

  const onNewMessage = useCallback((callback: (message: any) => void) => {
    socketRef.current?.on("new:message", callback);
    return () => {
      socketRef.current?.off("new:message", callback);
    };
  }, []);

  const onConversationUpdated = useCallback((callback: (data: any) => void) => {
    socketRef.current?.on("conversation:updated", callback);
    return () => {
      socketRef.current?.off("conversation:updated", callback);
    };
  }, []);

  const onConversationNew = useCallback((callback: (data: any) => void) => {
    socketRef.current?.on("conversation:new", callback);
    return () => {
      socketRef.current?.off("conversation:new", callback);
    };
  }, []);

  const onTyping = useCallback((callback: (data: any) => void) => {
    socketRef.current?.on("typing", callback);
    return () => {
      socketRef.current?.off("typing", callback);
    };
  }, []);

  const onNotification = useCallback((callback: (data: any) => void) => {
    socketRef.current?.on("notification:new", callback);
    return () => {
      socketRef.current?.off("notification:new", callback);
    };
  }, []);

  return {
    socket: socketRef.current,
    connected,
    joinConversation,
    leaveConversation,
    sendTyping,
    onNewMessage,
    onConversationUpdated,
    onConversationNew,
    onTyping,
    onNotification,
  };
}
